import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { Flag, Trash2 } from "lucide-react";
import { api, useApiMutation, useApp, useBreakdown, useGoals, type Goal } from "../lib/api";
import { useCan } from "../lib/access";
import { fmtNumber } from "../lib/format";
import { track } from "../lib/track";
import { AppHeader, useAppId } from "../components/app-header";
import { Hesitation } from "../components/hesitation";
import { Badge, Button, Card, CardHeader, CardSection, Drawing, Empty, Field, IconButton, Input, NoAccess, SelectField, notifyError, useConfirm } from "../components/ui";

export function Goals() {
  const appId = useAppId();
  const goals = useGoals(appId);
  const events = useBreakdown(appId, "event", 20);
  const app = useApp(appId);
  const canEdit = useCan(appId)("goals.edit");
  const [kind, setKind] = useState<Goal["kind"]>("event");
  const [match, setMatch] = useState("");
  const [name, setName] = useState("");
  // The "Converting to" goal lives in the URL (?goal=, as on Converters) so the view can be linked to.
  const [params, setParams] = useSearchParams();
  const doubtGoal = params.get("goal") ?? "any";
  const setDoubtGoal = (v: string) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === "any") next.delete("goal");
      else next.set("goal", v);
      return next;
    });

  const invalidate = [["goals", appId], ["summary", appId], ["timeseries", appId], ["breakdown", appId], ["portfolio"]];
  const create = useApiMutation((g: { name: string; kind: Goal["kind"]; match: string }) => api<Goal>(`/apps/${appId}/goals`, { method: "POST", json: g }), invalidate);
  const remove = useApiMutation((id: string) => api(`/apps/${appId}/goals/${id}`, { method: "DELETE" }), invalidate);
  const { confirm, dialog } = useConfirm();

  async function deleteGoal(g: Goal, i: number) {
    const list = goals.data ?? [];
    const neighbour = list[i + 1] ?? list[i - 1];
    const ok = await confirm({
      title: `Delete goal “${g.name}”?`,
      description: "Conversion rate and revenue attribution stop counting it across every report. Your event data isn't affected.",
      // Land on the neighbouring goal's delete button, or the add form when this was the last one.
      focusAfter: () => document.getElementById(neighbour ? `goal-delete-${neighbour.id}` : "goal-match"),
    });
    if (!ok) return;
    remove.mutate(g.id, { onSuccess: () => track("goal_deleted"), onError: notifyError });
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await create.mutateAsync({ kind, match: match.trim(), name: name.trim() || match.trim().slice(0, 80) });
      track("goal_created", { kind, source: "form" });
      setMatch("");
      setName("");
    } catch (err) {
      notifyError(err);
    }
  };

  // A deleted goal falls back to any goal.
  const doubtOn = goals.data?.some((g) => g.id === doubtGoal) ? doubtGoal : "any";
  const existing = new Set(goals.data?.filter((g) => g.kind === "event").map((g) => g.match));
  const suggestions = (events.data ?? []).filter((e) => !e.key.startsWith("$") && !existing.has(e.key)).slice(0, 6);

  return (
    <>
      <AppHeader title="Goals" subtitle="Goals define what a conversion is. They power conversion rate, revenue attribution and CR by source across every report." />
      <div className="grid gap-4 @4xl:grid-cols-[1fr_360px]">
        <div className="min-w-0 space-y-4">
          <Card className="h-fit">
            <CardHeader title="Goals" />
            {goals.data?.length ? (
              <ul className="divide-y divide-line border-t border-line">
                {goals.data.map((g, i) => (
                  <li key={g.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <div className="flex min-w-0 items-center gap-3">
                      <Flag size={15} className="shrink-0 text-accent-text" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-ink">{g.name}</div>
                        <div className="truncate font-mono text-xs text-ink-3">
                          {g.kind === "event" ? "Event" : "Page"} = {g.match}
                        </div>
                      </div>
                    </div>
                    {canEdit && (
                      <IconButton id={`goal-delete-${g.id}`} label={`Delete goal ${g.name}`} tone="danger" onClick={() => deleteGoal(g, i)}>
                        <Trash2 size={14} aria-hidden />
                      </IconButton>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <Empty title="No goals yet" illustration={<Drawing name="createFirst" />}>Add a sign-up, purchase or key page as a goal to start measuring conversion.</Empty>
            )}
          </Card>
          {/* Doubt before deciding, for any goal or one of them. */}
          {goals.data?.length ? (
            <div className="space-y-2">
              {/* Label beside the picker; the <label> names it. */}
              <label className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-ink-2">Converting to</span>
                <SelectField
                  className="w-56"
                  items={[{ value: "any", label: "Any goal" }, ...goals.data.map((g) => ({ value: g.id, label: g.name }))]}
                  value={doubtOn}
                  onChange={setDoubtGoal}
                />
              </label>
              <Hesitation appId={appId} target={doubtOn !== "any" ? { goal: doubtOn } : {}} />
            </div>
          ) : null}
        </div>
        {canEdit ? (
          <div className="space-y-4">
            <Card>
              <CardHeader title="Add goal" />
              <CardSection>
                <form onSubmit={submit} className="space-y-3">
                  <Field label="Type">
                    <SelectField className="w-full" items={[{ value: "event", label: "Custom event" }, { value: "page", label: "Page visit" }]} value={kind} onChange={(v) => setKind(v as Goal["kind"])} />
                  </Field>
                  <Field label={kind === "event" ? "Event name" : "Page path"} hint={kind === "page" ? "Use a trailing * for prefixes, e.g. /thank-you*" : undefined}>
                    <Input id="goal-match" required maxLength={512} className="font-mono" placeholder={kind === "event" ? "signup" : "/thank-you"} value={match} onChange={(e) => setMatch(e.target.value)} />
                  </Field>
                  <Field label="Display name">
                    <Input maxLength={80} placeholder="Signed up" value={name} onChange={(e) => setName(e.target.value)} />
                  </Field>
                  <Button variant="primary" pending={create.isPending} pendingLabel="Adding…">Add goal</Button>
                </form>
              </CardSection>
            </Card>
            {suggestions.length > 0 && (
              <Card>
                <CardHeader title="Suggested from your events" />
                <CardSection flush className="space-y-1 p-2">
                  {suggestions.map((s) => (
                    <button
                      key={s.key}
                      className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left hover:bg-surface-2"
                      onClick={() => create.mutate({ kind: "event", match: s.key, name: s.key }, { onSuccess: () => track("goal_created", { kind: "event", source: "suggestion" }) })}
                    >
                      <span className="font-mono text-[13px] text-ink">{s.key}</span>
                      <Badge>{fmtNumber(s.visitors)} visitors</Badge>
                    </button>
                  ))}
                </CardSection>
              </Card>
            )}
          </div>
        ) : (
          app.data && (
            <NoAccess className="h-fit">You can view goals. Ask a workspace admin for permission to change them.</NoAccess>
          )
        )}
      </div>
      {dialog}
    </>
  );
}
